import { NON_BUSINESS_VEHICLE_CODES, GBP_CURRENCY_CONFIG } from './constants';

/**
 * Currency formatter instance for consistent British Pound formatting
 */
export const currencyFormatter = new Intl.NumberFormat(GBP_CURRENCY_CONFIG.locale, {
	style: GBP_CURRENCY_CONFIG.style,
	currency: GBP_CURRENCY_CONFIG.currency,
});

/**
 * Capitalizes the first letter of a string
 */
export const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

/**
 * Capitalizes the first letter of each word in a string
 */
export const capitalizeFirstLetterOfEachWord = (input: string): string => {
	if (!input) return '';
	return input.replace(/\b[a-z]/g, (char) => char.toUpperCase());
};

/**
 * Extracts the first integer found in a string
 */
export const getFirstInteger = (str: string): string => {
	const matches = str.match(/\d+/);
	if (matches && matches.length > 0) {
		return parseInt(matches[0], 10).toString();
	}
	return '';
};

/**
 * Converts a negative number to positive
 */
export const flipToPositive = (num: number): number => {
	return num < 0 ? -num : num;
};

/**
 * Formats a number as British pounds currency
 */
export const formatPounds = (value: number) => {
	if (value === undefined) return '';

	const formatter = new Intl.NumberFormat(GBP_CURRENCY_CONFIG.locale, {
		style: GBP_CURRENCY_CONFIG.style,
		currency: GBP_CURRENCY_CONFIG.currency,
	});
	return formatter.format(value);
};

/**
 * Formats an amount as currency using the shared formatter
 */
export const formatCurrency = (amount: number) => currencyFormatter.format(amount);

/**
 * Adds a two-digit prefix to numbers less than 10
 */
export function minTwoDigits(n: number) {
	return (n < 10 ? '0' : '') + n.toString();
}

type OrdinalSuffix = 'st' | 'nd' | 'rd' | 'th';
type OrdinalMonth = `${number}${OrdinalSuffix}`;

/**
 * Adds ordinal suffix to a number (1st, 2nd, 3rd, etc.)
 */
export function addOrdinalSuffix(input: number | undefined): OrdinalMonth | undefined {
	if (input === undefined) return;
	const j = input % 10,
		k = input % 100;
	if (j == 1 && k != 11) {
		return `${input}st`;
	}
	if (j == 2 && k != 12) {
		return `${input}nd`;
	}
	if (j == 3 && k != 13) {
		return `${input}rd`;
	}
	return `${input}th`;
}

/**
 * Formats a Date object as dd/MM/yyyy string
 */
export function formatDate(isoDate: Date): string {
	const date = new Date(isoDate);
	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();

	return `${day}/${month}/${year}`;
}

// Re-export constants for direct access
export { NON_BUSINESS_VEHICLE_CODES };
